/** @jsx jsx */

import { Fragment as _, useState, useContext, cloneElement } from 'react';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import { useDraggable } from '@dnd-kit/core';
import { interactionContext } from './interactionContext';
import { useSelector } from 'react-redux';
import hexToRgb from 'services/hexToRGB';
import { usePubSub } from 'services/pubSub';

const { lPink, lGrey0, lGrey1, lGrey2 } = colors;

const MatchDrag = props => {
  const {
    draggableId,
    isDragDisabled = false,
    isDropDisabled = false,
    dropZoneColor,
    // style,
    children,
  } = props;

  const context = useContext(interactionContext);

  const {
    panelBackground: _panelBackground = '#fff',
    panelBackgroundOpacity: _panelBackgroundOpacity = 100,
    panelBackgroundDragging: _panelBackgroundDragging,
    panelBackgroundDraggingOpacity: _panelBackgroundDraggingOpacity = 100,
    textColorDragging = '#fff',
    panelWidth = 250,
    panelHeight = 250,
    panelOutline = false,
    panelOutlineColor = '',
    panelBorderRadius = 5,
    panelShadow = true,
    focusColor = props.panelBackground,
  } = context.props;

  const { r, g, b } = hexToRgb(_panelBackground);
  const panelBackgroundOpacity = _panelBackgroundOpacity * 0.01;
  const panelBackground = `rgba(${r}, ${g}, ${b}, ${panelBackgroundOpacity})`;

  const { r: rr, g: gg, b: bb } = hexToRgb(_panelBackgroundDragging);
  const panelBackgroundDraggingOpacity = _panelBackgroundDraggingOpacity * 0.01;
  const panelBackgroundDragging = `rgba(${rr}, ${gg}, ${bb}, 1)`;

  let [media] = children;

  if (media) {
    media = cloneElement(media, {
      onMediaChange(child) {
        mediaTypeUpdated(child);
      },
    });
  }

  let initialMediaType = media?.props?.child ?? 1;
  const [mediaType, mediaTypeUpdated] = useState<number>(initialMediaType);

  // this used as the key to have the fricken image update
  // grabbing the src off the img directly from props doesn't kick in
  // so. going to the SOURCE of the src
  const keyFromSrc =
    window.dev() &&
    useSelector(
      (state: any) => state.blocks[children[0].props.children[1].props.id].props
    ).src;

  const [hoverHilite, publish] = usePubSub(
    `MATCHING_INTERACTION_FEEDBACK_${context.id}`
  );

  const hover =
    hoverHilite &&
    hoverHilite.draggableId === draggableId &&
    hoverHilite.eventType === 'mouseOver';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `${props.id}`,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const id = `match-drag-${props?.id}`;

  return (
    <div
      className="droppable-container"
      css={css`
        position: relative;
        width: ${panelWidth}px;
        height: ${panelHeight}px;
        margin: 10px;
        text-align: center;
        background: ${'transparent' || '#fff' || lGrey1};
        border-radius: ${panelBorderRadius}px;
      `}
    >
      <div
        ref={setNodeRef}
        id={id}
        className={!isDragDisabled ? 'match-drag-focusable' : ''}
        css={css`
        position: relative;
                    width: ${panelWidth}px;
                    height: ${panelHeight}px;
                    text-align: center;
                    ${isDragDisabled &&
                      `.draggable-content {
            box-shadow: 0 0 0 1px ${lGrey2};
          }`}

          touch-action: none;

                    border-radius: ${panelBorderRadius}px;
                    ${!isDragging &&
                      `&:focus {
                              //
                              ${getButtonFocus(
                                ['#fff', '#ffffff'].includes(panelBackground)
                                  ? lGrey0
                                  : focusColor || panelBackground
                              )}
                            }`}

                            pointer-events: ${isDragDisabled ? 'none' : 'auto'};
                  `}
        style={{
          zIndex: isDragging ? 2147483647 : 0,
          cursor: window.dev() ? 'auto' : 'grab',
          ...style,
        }}
        {...(!window.dev() &&
          !isDragDisabled && {
            ...listeners,
            ...attributes,
          })}
      >
        <div
          className="draggable-content matchingPanel"
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: ${panelWidth}px;
            height: ${panelHeight}px;
            padding: 10px;
            padding: ${mediaType === 0 ? '10px' : '0px'};

            border: 1px solid
              ${panelOutline && panelOutlineColor
                ? panelOutlineColor
                : 'transparent'};

            border-radius: ${panelBorderRadius}px;
            background: ${isDragging
              ? panelBackgroundDragging
              : panelBackground};
            box-shadow: ${panelShadow ? '0 0 15px rgba(0, 0, 0, 0.3)' : 'none'};
            transition: background 0.2s, box-shadow 0.2s;
            overflow: hidden;

            .chameleon-image > div,
            .chameleon-image img {
              width: ${panelWidth}px !important;
              height: ${panelHeight}px !important;
            }

            * {
              ${isDragging && `color: ${textColorDragging} !important;`}
            }
            ${!window.dev() &&
              `
                          user-select: none;
                      `}

            ${window.dev() &&
              hover &&
              `
                      transition: border 0.3s;
                      border: 1px solid ${lPink};
                      `}
          `}
        >
          {media}
        </div>
      </div>
    </div>
  );
};

export default MatchDrag;
